import {
  KB_CATEGORY_CODES,
  type KbArticle,
  type KbAudience,
  type KbCategoryCode,
} from '@paguz/contracts';
import { Badge, Button, cn, inputBaseClass, toast } from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  AlertTriangleIcon,
  ArchiveIcon,
  ArrowLeftIcon,
  GitCompareIcon,
  SaveIcon,
  SendIcon,
  UploadCloudIcon,
} from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ConfirmActionDialog } from '../../components/confirm-dialog.tsx';
import { Field, Select, Textarea, compactInputClass } from '../../components/form-controls.tsx';
import { DiffView, Markdown } from '../../components/markdown.tsx';
import { ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { useCan, useSession } from '../../lib/session.ts';
import { KB_AUDIENCE, KB_CATEGORY, KB_VERSION_STATUS } from '../agents/labels.ts';

const route = getRouteApi('/app/conocimiento/$articleId');

export function ArticlePage() {
  const { articleId } = route.useParams();
  const query = useQuery({
    queryKey: queryKeys.kbArticle(articleId),
    queryFn: () => api.getKbArticle(articleId),
  });
  return (
    <>
      <Link
        to="/conocimiento"
        className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        <ArrowLeftIcon className="size-4" /> Base de conocimiento
      </Link>
      {query.isPending ? (
        <LoadingRows rows={8} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <ArticleEditor
          key={`${query.data.id}-${query.data.latestVersion.version}-${query.data.latestVersion.status}`}
          article={query.data}
        />
      )}
    </>
  );
}

function ArticleEditor({ article }: { article: KbArticle }) {
  const canEdit = useCan('knowledge:edit');
  const canPublish = useCan('knowledge:publish');
  const session = useSession();
  const queryClient = useQueryClient();
  const latest = article.latestVersion;
  const [title, setTitle] = useState(article.title);
  const [category, setCategory] = useState<KbCategoryCode>(article.category);
  const [audience, setAudience] = useState<KbAudience>(article.audience);
  const [content, setContent] = useState(latest.contentMd);
  const [note, setNote] = useState('');
  const [publishing, setPublishing] = useState(false);
  const dirty =
    title !== article.title ||
    category !== article.category ||
    audience !== article.audience ||
    content !== latest.contentMd;

  const onSaved = (next: KbArticle, message: string) => {
    queryClient.setQueryData(queryKeys.kbArticle(article.id), next);
    void queryClient.invalidateQueries({ queryKey: queryKeys.kbArticles });
    void queryClient.invalidateQueries({ queryKey: queryKeys.kbVersions(article.id) });
    toast.success(message);
  };
  const save = useMutation({
    mutationFn: () =>
      api.updateKbArticle(article.id, {
        ...(title !== article.title && { title }),
        ...(category !== article.category && { category }),
        ...(audience !== article.audience && { audience }),
        ...(content !== latest.contentMd && { contentMd: content }),
        ...(note.trim() && { changeNote: note.trim() }),
      }),
    onSuccess: (next) => {
      onSaved(next, `Borrador v${next.latestVersion.version} guardado`);
    },
    onError: (e) => toast.error(describeError(e)),
  });
  const transition = useMutation({
    mutationFn: (action: 'submit-review' | 'publish' | 'archive') =>
      api.kbTransition(article.id, action),
    onSuccess: (next, action) => {
      onSaved(
        next,
        action === 'publish'
          ? `v${next.latestVersion.version} publicada: los agentes ya la usan`
          : action === 'archive'
            ? 'Artículo archivado'
            : 'Enviado a revisión',
      );
    },
    onError: (e) => toast.error(describeError(e)),
  });
  const sameAuthor = latest.authorId !== null && latest.authorId === session?.user.id;

  return (
    <>
      <PageHeader
        title={article.title}
        description={`${KB_CATEGORY[article.category]} · ${KB_AUDIENCE[article.audience]} · ${article.slug}`}
        actions={
          <>
            {article.publishedVersion && (
              <Badge variant="success">Publicada v{article.publishedVersion}</Badge>
            )}
            <Badge variant={KB_VERSION_STATUS[latest.status].tone}>
              v{latest.version} · {KB_VERSION_STATUS[latest.status].label}
            </Badge>
          </>
        }
      />
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel
          title="Editor"
          description={
            latest.status === 'DRAFT'
              ? 'Editas el borrador abierto.'
              : 'Guardar crea un borrador nuevo; la versión publicada sigue vigente hasta publicar otra.'
          }
          actions={
            canEdit && (
              <Button
                variant="outline"
                disabled={!dirty || save.isPending}
                onClick={() => {
                  save.mutate();
                }}
              >
                <SaveIcon /> Guardar borrador
              </Button>
            )
          }
        >
          <fieldset disabled={!canEdit} className="flex flex-col gap-4">
            <Field label="Título" htmlFor="art-title">
              <input
                id="art-title"
                className={cn(inputBaseClass, compactInputClass)}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                }}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Categoría" htmlFor="art-cat">
                <Select
                  id="art-cat"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value as KbCategoryCode);
                  }}
                >
                  {KB_CATEGORY_CODES.map((c) => (
                    <option key={c} value={c}>
                      {KB_CATEGORY[c]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Audiencia" htmlFor="art-aud">
                <Select
                  id="art-aud"
                  value={audience}
                  onChange={(e) => {
                    setAudience(e.target.value as KbAudience);
                  }}
                >
                  {(['BOTH', 'CUSTOMER', 'AGENT'] as const).map((a) => (
                    <option key={a} value={a}>
                      {KB_AUDIENCE[a]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="Contenido (markdown)" htmlFor="art-md">
              <Textarea
                id="art-md"
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                }}
                className="min-h-80 font-mono text-xs"
              />
            </Field>
            <Field label="Nota del cambio" htmlFor="art-note">
              <input
                id="art-note"
                className={cn(inputBaseClass, compactInputClass)}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
                placeholder="Qué cambió y por qué"
              />
            </Field>
          </fieldset>
          <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4">
            {canEdit && latest.status === 'DRAFT' && (
              <Button
                variant="outline"
                disabled={dirty || transition.isPending}
                title={dirty ? 'Guarda primero el borrador' : undefined}
                onClick={() => {
                  transition.mutate('submit-review');
                }}
              >
                <SendIcon /> Enviar a revisión
              </Button>
            )}
            {canPublish && latest.status === 'IN_REVIEW' && (
              <Button
                variant="highlight"
                disabled={transition.isPending}
                onClick={() => {
                  setPublishing(true);
                }}
              >
                <UploadCloudIcon /> Publicar v{latest.version}
              </Button>
            )}
            {canPublish && article.status === 'PUBLISHED' && (
              <Button
                variant="ghost"
                disabled={transition.isPending}
                onClick={() => {
                  transition.mutate('archive');
                }}
              >
                <ArchiveIcon /> Archivar
              </Button>
            )}
          </div>
        </Panel>
        <Panel title="Vista previa">
          <Markdown source={content} />
        </Panel>
      </div>
      <Versions
        article={article}
        onRestore={(md) => {
          setContent(md);
        }}
      />
      <ConfirmActionDialog
        open={publishing}
        onOpenChange={setPublishing}
        title={`Publicar la versión ${latest.version}`}
        description="Crea una versión inmutable; los agentes la usan desde ahora y citan este número de versión."
        confirmLabel="Publicar"
        requireReason={false}
        extra={
          sameAuthor && (
            <p className="flex gap-2 rounded-xl bg-warning-soft px-3 py-2 text-sm text-warning-soft-foreground">
              <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" /> Eres el autor de esta
              versión. En producción la publica un revisor distinto; en el demo se permite con esta
              advertencia.
            </p>
          )
        }
        onConfirm={async () => {
          await transition.mutateAsync('publish');
        }}
      />
    </>
  );
}

function Versions({ article, onRestore }: { article: KbArticle; onRestore: (md: string) => void }) {
  const canEdit = useCan('knowledge:edit');
  const query = useQuery({
    queryKey: queryKeys.kbVersions(article.id),
    queryFn: () => api.listKbVersions(article.id),
  });
  const [compare, setCompare] = useState<number | null>(null);
  const versions = query.data?.data ?? [];
  const latest = versions[0];
  const other = versions.find((x) => x.version === compare);
  return (
    <Panel
      className="mt-6"
      title="Versiones"
      description="Cada publicación es inmutable. Compara contra la última o restaura una versión como borrador."
    >
      {query.isPending ? (
        <LoadingRows rows={3} />
      ) : (
        <div className="flex flex-col gap-4">
          <ul className="flex flex-col gap-2">
            {versions.map((ver) => (
              <li key={ver.version} className="flex flex-wrap items-center gap-3 text-sm">
                <Badge variant={KB_VERSION_STATUS[ver.status].tone}>
                  v{ver.version} · {KB_VERSION_STATUS[ver.status].label}
                </Badge>
                <span className="text-muted-foreground">
                  {formatDateTime(ver.publishedAt ?? ver.createdAt)}
                </span>
                <span className="min-w-0 flex-1 truncate">{ver.changeNote ?? '—'}</span>
                {latest && ver.version !== latest.version && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setCompare(compare === ver.version ? null : ver.version);
                      }}
                    >
                      <GitCompareIcon /> Comparar con v{latest.version}
                    </Button>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          onRestore(ver.contentMd);
                          toast.info(
                            `Contenido de v${ver.version} cargado en el editor; guarda para crear el borrador.`,
                          );
                        }}
                      >
                        Restaurar
                      </Button>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
          {other && latest && (
            <DiffView
              before={other.contentMd}
              after={latest.contentMd}
              labels={[`v${other.version}`, `v${latest.version}`]}
            />
          )}
        </div>
      )}
    </Panel>
  );
}
